import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { BellOff } from "lucide-react";
import useNotificationsApi from "../../hooks/useNotificationsApi.js";
import DataTable from "../reuseable/DataTable";
import PaginationControls from "../reuseable/PaginationControls";
import TableSkeletonRow from "../reuseable/TableSkeletonRow";

const NotificationSubscriberManagement = forwardRef((props, ref) => {
  const { fetchSubscribersApi, unsubscribeApi } = useNotificationsApi();

  const [subscribers, setSubscribers] = useState([]);
  const [page, setPage] = useState(1);
  const perPage = 10;
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [unsubscribingIds, setUnsubscribingIds] = useState([]);

  const fetchSubscribers = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await fetchSubscribersApi();
      setSubscribers(data);
    } catch (err) {
      console.error("Fetch error", err);
    } finally {
      setIsLoading(false);
    }
  }, [fetchSubscribersApi]);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  useImperativeHandle(ref, () => ({
    fetchSubscribers,
  }));

  const filteredSubscribers = subscribers.filter((s) =>
    s.email.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filteredSubscribers.length / perPage) || 1;
  const paginatedSubscribers = filteredSubscribers.slice(
    (page - 1) * perPage,
    page * perPage
  );
  const shouldShowSkeleton = isLoading && subscribers.length === 0;

  const unsubscribeSubscriber = async (id) => {
    try {
      setUnsubscribingIds((prev) => [...prev, id]);

      await unsubscribeApi(id);

      setSubscribers((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      console.error("Unsubscribe error", err);
      alert("Failed to unsubscribe subscriber");
    } finally {
      setUnsubscribingIds((prev) => prev.filter((value) => value !== id));
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const columns = [
    {
      key: "email",
      label: "Email",
      render: (sub) => sub.email,
      cellStyle: {
        fontWeight: "500",
        color: "#333",
      },
    },
    {
      key: "subscribedAt",
      label: "Subscribed Date",
      render: (sub) => formatDate(sub.subscribedAt),
      cellStyle: { color: "#666" },
    },
    {
      key: "actions",
      label: "Actions",
      headerStyle: { textAlign: "left", width: "10%" },
      cellStyle: { textAlign: "left" },
      render: (sub) => {
        const isUnsubscribing = unsubscribingIds.includes(sub.id);

        return (
          <div className="flex gap-2 justify-start">
            <button
              type="button"
              onClick={() => unsubscribeSubscriber(sub.id)}
              disabled={isUnsubscribing}
              className="inline-flex items-center gap-2 rounded-md border border-red-300/80 bg-red-100 px-3 py-1 text-sm font-semibold text-red-700 hover:border-red-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {!isUnsubscribing && <BellOff className="w-4 h-4" />}
              {isUnsubscribing ? "Unsubscribing..." : "Unsubscribe"}
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div  style={{
        background: "#fff",
        borderRadius: 12,
        boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
        padding: 32,
        margin: "0 auto",
      }}>
      <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
        <input
          type="text"
          placeholder="Search by email..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          disabled={isLoading}
          style={{
            padding: "10px 14px",
            flex: 1,
            borderRadius: "6px",
            border: "1px solid #ddd",
            backgroundColor: isLoading ? "#f0f0f0" : "white",
            cursor: isLoading ? "not-allowed" : "auto",
            fontSize: "14px",
          }}
        />
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
        <strong style={{ fontSize: "14px", color: "#666" }}>
          Total Subscribers: {filteredSubscribers.length}
        </strong>
        <PaginationControls
          page={page}
          totalPages={totalPages}
          isLoading={isLoading}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
          style={{ fontSize: "12px" }}
        />
      </div>

      <div style={{ overflowX: "auto" }}>
        <DataTable
          columns={columns}
          rows={paginatedSubscribers}
          loading={shouldShowSkeleton}
          skeletonRowCount={5}
          emptyMessage={<span style={{ color: "#999", fontSize: "15px" }}>📭 No subscribers found</span>}
          tableStyle={{ marginTop: 16 }}
          rowKey={(row, idx) => row.id ?? row.email ?? `subscriber-row-${idx}`}
          rowProps={(_, idx) => ({
            style: {
              background: idx % 2 === 0 ? "#ffffff" : "#f3f0f7",
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              border: "1px solid #e5e5e5",
              transition: "all 0.3s ease",
            },
            onMouseEnter: (e) => {
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(187,28,204,0.15)";
              e.currentTarget.style.transform = "translateY(-2px)";
            },
            onMouseLeave: (e) => {
              e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)";
              e.currentTarget.style.transform = "translateY(0)";
            },
          })}
          loadingRow={(idx) => (
            <TableSkeletonRow
              idx={idx}
              rowKeyPrefix="subscriber-skeleton"
              columnCount={columns.length}
              actionColumnIndex={columns.length - 1}
              cellWidths={["10rem", "8rem", "8rem"]}
            />
          )}
        />
      </div>
    </div>
  );
});

NotificationSubscriberManagement.displayName = "NotificationSubscriberManagement";

export default NotificationSubscriberManagement;